import { LitElement, html, css } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import dayjs from 'dayjs';
import { actions, selectors } from './';
import { connect } from '../store/connect';
import { SELECT_MODES } from '../table/table-constants';
import '@trumedianetworks/tmn-core-components/src/tmn-input';
import '@trumedianetworks/tmn-core-components/src/tmn-switch';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-tooltip';
import '../table/tmn-table-editable';
import './site-token-detail';
import { AUTHORIZATIONS } from '../authorizations/authorizations';
const { CAN_MANAGE_MASTER_TOKENS } = AUTHORIZATIONS;

const dateFormatFn = (value, row) => {
    return value
        ? html`${dayjs(value).format('YYYY-MM-DD')}`
        : html`<div style="text-align:center">-</div>`;
};

class SiteTokenList extends LitElement {
    constructor() {
        super();
        this._columnDefs = [];
    }

    static get styles() {
        // can be shared with site or more general table component maybe?
        return [
            css`
                :host {
                    --table-cell-height: 36px;
                }
                .message-wrapper {
                    display: flex;
                    margin-top: 150px;
                    align-items: center;
                    justify-content: center;
                }
                .message-wrapper .message {
                    border-radius: 8px;
                    padding: 40px;
                    font-size: 20px;
                    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px,
                        rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            tokens: {
                type: Array,
            },
            authorizations: {
                type: Array,
            },
            _columnDefs: {
                type: Array,
            },
            selectedTokens: {
                type: Array,
            },
            detailMode: {
                type: String,
            },
            selectMode: {
                type: String,
            },
            siteGuid: {
                type: String,
            },
        };
    }

    firstUpdated(changedProps) {
        // todo if we allowed per-site managment rules, could adjust based on something  in updated
        // if (changedProps.has('authorizations')) {
        this._columnDefs = [
            {
                label: 'site',
                key: 'name',
                sortable: true,
                search: true,
                hideInDetailMode: true,
            },
            {
                key: 'token',
                label: 'token',
                sortable: true,
                search: true,
                hideInDetailMode: true,
                dataFormatter: (token) => '...' + token.substring(token.length - 12),
            },
            {
                key: 'expiration_date',
                label: 'Expiration',
                sortable: true,
                search: true,
                hideInDetailMode: true,
                dataFormatter: dateFormatFn,
            },
            {
                key: 'last_used',
                label: 'Last used',
                sortable: true,
                search: true,
                hideInDetailMode: true,
                dataFormatter: dateFormatFn,
            },
            {
                key: 'is_expired',
                label: 'Expired',
                sortable: true,
                search: true,
                hideInDetailMode: true,
                dataFormatter: (val) => Boolean(val),
            },
            {
                key: 'kid',
                label: 'kid',
                sortable: true,
                search: true,
                hideInDetailMode: true,
            },
            {
                key: 'ts',
                label: 'Created',
                sortable: true,
                search: false,
                hideInDetailMode: true,
                dataFormatter: dateFormatFn,
            },
        ];
    }

    updated(changedProps) {
        // for now, just manage the state in this component
        // since it isn't really shared with the rest of the app
        if (changedProps.has('tokens')) {
            this.selectedTokens = [];
        }
    }

    render() {
        return this.renderTable();
    }

    // needed to avoid triggering the row click
    stopPropagation(e) {
        if (e) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
        }
    }

    renderTable() {
        const { tokens = [], _columnDefs, selectedTokens = [], detailMode } = this;
        return html`
            <div class="table-container">
                <tmn-table-editable
                    @tmn-row-select-toggle=${this.handleRowSelectToggle}
                    @tmn-table-editable-close-details=${this.handleCancelOperation}
                    @token-edit-cancel=${this.handleCancelOperation}
                    @token-edit-save=${this.handleSave}
                    .useFilters=${false}
                    .useSearch=${false}
                    .rowIdentifier=${'token'}
                    .dataRows=${tokens}
                    .selectedRowIds=${selectedTokens}
                    .detailMode=${detailMode}
                    .canAddNew=${false}
                    .selectMode=${SELECT_MODES.SINGLE}
                    .shouldConfirmOnClose=${false}
                    .shouldConfirmOnNavigate=${false}
                    .confirmMessage=${'The selected token record appears to have changes.  If you continue without saving the changes will be lost.'}
                    .columnDefs=${_columnDefs}>
                    <div slot="left-actions">
                        <tmn-button
                            color="primary"
                            name="new"
                            variant="contained"
                            @click=${this.handleNewToken}>
                            Generate New Token
                        </tmn-button>
                    </div>
                    ${this.renderDetail()}
                </tmn-table-editable>
            </div>
        `;
    }

    renderDetail() {
        const { tokens, selectedTokens = [], authorizations } = this;
        const [selectedToken] = selectedTokens;
        const tokenRec = tokens.find((rec) => rec.token === selectedToken);
        return html`<site-token-detail
            .tokenData=${tokenRec}
            .authorizations=${authorizations}
            slot="detail"></site-token-detail>`;
    }

    handleNewToken(e) {
        this.stopPropagation(e);
        const { siteGuid } = this;
        if (window.confirm('Create new master token?')) {
            this.actions.generateNewSiteToken({ guid: siteGuid });
        }
    }

    handleCancelOperation(e) {
        this.stopPropagation(e);
        this.selectedTokens = [];
    }

    handleRowSelectToggle(e) {
        this.stopPropagation(e);
        const { detail = {} } = e;
        const { id, selected } = detail;
        if (selected) {
            this.selectedTokens = [id];
        } else {
            this.selectedTokens = [];
        }
    }

    handleSave(e) {
        this.stopPropagation(e);
        const { siteGuid } = this;
        const { token, expiration_date, guid } = e.detail;
        this.actions.saveSiteToken({ token, expiration_date, guid: siteGuid });
    }
}

customElements.define('site-token-list', connect(null, actions)(SiteTokenList));
